import {FileBuilder} from '../../utils/fileBuilder'
import {buildReplaceRegex} from '../../utils/utils'
import {
    CSS_DECLARATION_PROPERTY_RESERVED_CHARS,
    CSS_DECLARATION_VALUE_RESERVED_CHARS,
    CSS_RESERVED_CHARS_PREFIX,
    type CssProperty,
    type CssValue
} from './constants'
import type {WmlFileContent} from '@wix/document-services-types'

/**
 * Adds a prefix to reserved chars to solve parsing issues.
 * For example:
 * #c1dmp {
 *   property-na:me: Property V;alue;
 * }
 *
 * In this case since the parser is relying on : and ; to parse the declaration it won't be able to parse it correctly,
 * and therefore we are adding a prefix like that:
 * #c1dmp {
 *   property-na\:me: Property V\;alue;
 * }
 */
const addPrefixToReservedChars = (value: string, regex: RegExp): string => {
    return value.replace(regex, (charToReplace: string) => CSS_RESERVED_CHARS_PREFIX + charToReplace)
}

const buildReservedCharsRegex = (reservedChars: string[]): RegExp =>
    buildReplaceRegex([CSS_RESERVED_CHARS_PREFIX, ...reservedChars])

const propertyRegex = buildReservedCharsRegex(CSS_DECLARATION_PROPERTY_RESERVED_CHARS)
const valueRegex = buildReservedCharsRegex(CSS_DECLARATION_VALUE_RESERVED_CHARS)

export class CssFileBuilder {
    private fileBuilder = new FileBuilder()

    public startBlock(header: string) {
        this.fileBuilder.writeLine(`${header} {`)
        this.fileBuilder.indent()
    }

    public endBlock() {
        this.fileBuilder.dedent()
        this.fileBuilder.writeLine(`}`)
        this.fileBuilder.writeEmptyLine()
    }

    public writeProperty(property: CssProperty, value: CssValue) {
        this.fileBuilder.writeLine(
            `${addPrefixToReservedChars(property, propertyRegex)}: ${addPrefixToReservedChars(value, valueRegex)};`
        )
    }

    public build(): WmlFileContent {
        return this.fileBuilder.toString()
    }
}
